/*

  EdenredDataModel est un singleton
  Il gère l'authentification myEdenRed (Ticket Restaurant)

   */
import LocalStorageModel from "@src/backbone/prototypes/LocalStorageModel";
import { app } from "@src/backbone/model/AppModel";
import PaymentModeModel, {
  EPaymentModeDisableDetailsType,
  PaymentModePicture,
  PMDisabledDetails,
} from "@src/backbone/model/PaymentModeModel";
import formatPrice from "@src/backbone/prototypes/formatPrice";
import ApiRequest from "@src/backbone/prototypes/ApiRequest";
import type { EdenredAuthSuccess } from "@src/components/page-content/Checkout/PaymentMode/EdenredProvider/EdenredAuthModal";
import { EAlertBlockVariant } from "design-system/src/Core/AlertBlock/AlertBlock";

const EdenredWhere = { class_payment_module: "Edenred" },
  cartIgnoreExtraPrice = { ignoreExtraPrices: true };
enum EdenredSyncState {
  PENDING_STATE = "pending",
  FETCHED_STATE = "fetched",
  ERROR_STATE = "error",
}

export interface EdenredButtonOptions {
  disabled: boolean;
  tagName: string;
  loading: boolean;
  desc_primary: string;
  desc_secondary: string;
  showSwitcher: boolean;
  change_label: string;
  class_list: string;
  warnings: PMDisabledDetails[];
  pict?: PaymentModePicture;
  authUrl?: string;
  auth: boolean;
  dark: boolean;
  checked: boolean;
}

function getCartPriceTTC(withoutExtraPrice: boolean = false) {
  const cart = app.getCart();
  let options;
  if (withoutExtraPrice) {
    options = cartIgnoreExtraPrice;
  }
  return cart.getPriceTTC(options);
}

enum PriceReturnType {
  BASE_AVAILABLE = "baseAvailable",
  ALT_LEFT_CAGNOTTED_USED = "altLeftCagnottedUsed",
  ALT_LEFT_TO_PAY = "altLeftToPay",
  EDENRED_CAGNOTTE_USED = "edenredCagnotteUsed",
  EDENRED_TO_PAY = "edenredToPay",
  TOTAL_LEFT_TO_PAY = "totalLeftToPay",
}
const getPrices = (returnType: PriceReturnType) => {
  const cart = app.getCart(),
    paymentMode = getPaymentMode(),
    // l'exemple utilise en commentaire un panier total 26,70 dont 1,50 de frais de gestion, et 1 euro payé en cagnotte
    // solde edenred à 25 euros

    totalPriceWithoutExtraPrice = getCartPriceTTC(true),
    // valeur du panier totale, sans les frais de gestion, sans cagnotte // 25,2

    extraPriceValue = app.getCart().getExtraPriceValue(),
    // valeur brute des frais de gestion // 1,5

    allow_fees = paymentMode?.getParam("allow_fees") || 0, // allow_fees = 1
    extraPriceValueWithException = allow_fees ? 0 : extraPriceValue, // 0
    extraPriceValueWithAllowFees = allow_fees ? extraPriceValue : 0, // 1,5
    // permet d'ignorer l'exception des frais de gestion (Belgique) // 0

    cagnotte = cart.getCagnotte() || 0,
    // cagnotte utilisée // 1

    extraPriceLeftToPay = Math.max(0, extraPriceValueWithException - cagnotte),
    // frais de gestion restant à payer // 0

    extraPriceCagnotteUsed = extraPriceValueWithException - extraPriceLeftToPay,
    // frais de gestion utilisé par la cagnotte // 0

    cagnotteLeft = cagnotte - extraPriceCagnotteUsed,
    // restant de cagnotte // 1

    available = EdenredDataModel.getInstance().getAvailableAmount(),
    // crédit edenred // 25

    totalPricePayable =
      totalPriceWithoutExtraPrice + extraPriceValueWithAllowFees, // 26,7
    baseAvailable = Math.min(available, totalPricePayable),
    // maximum edenred sans notion de cagnotte // 25

    baseAltLeft = totalPricePayable - baseAvailable,
    // restant à payer de base sans notion de cagnotte // 1,7

    altLeftToPay = Math.max(0, baseAltLeft - cagnotteLeft),
    // total non edenred restant à payer sans cagnotte // 0,7

    altLeftCagnottedUsed = baseAltLeft - altLeftToPay,
    // 1

    edenredCagnotteUsed =
      cagnotte - extraPriceCagnotteUsed - altLeftCagnottedUsed,
    // cagnotte utilisable sur edenred // 0

    edenredToPay = baseAvailable - edenredCagnotteUsed,
    // payable via edenred 25

    totalLeftToPay = altLeftToPay + extraPriceLeftToPay;
  // total restant à payer // 0,7

  let r = 0;
  switch (returnType) {
    case PriceReturnType.BASE_AVAILABLE:
      r = baseAvailable;
      break;
    case PriceReturnType.ALT_LEFT_CAGNOTTED_USED:
      r = altLeftCagnottedUsed;
      break;
    case PriceReturnType.ALT_LEFT_TO_PAY:
      r = altLeftToPay;
      break;
    case PriceReturnType.EDENRED_CAGNOTTE_USED:
      r = edenredCagnotteUsed;
      break;
    case PriceReturnType.EDENRED_TO_PAY:
      r = edenredToPay;
      break;
    case PriceReturnType.TOTAL_LEFT_TO_PAY:
      r = totalLeftToPay;
      break;
  }

  return parseFloat(Number(r).toFixed(2));
};

const getPaymentGroup = () =>
  app.getPaymentGroups()?.findWhere({ location: 1 });

const getPaymentMode = (inContext: boolean = false) =>
  (inContext ? app.getCart() : app)
    ?.getPaymentModes()
    ?.findWhere({ provider: "EdenredProvider" }) as
    | PaymentModeModel
    | undefined;

const getPM = getPaymentMode;

const hasPM = (inContext: boolean = false) =>
  !!getPM(inContext) && !!getPaymentGroup();

class EdenredDataModel extends LocalStorageModel<{
  sync: EdenredSyncState;
  checked: boolean;
  disabled: boolean;
  availableAmount: string;
  login: string;
  auth: boolean;
  authUrl?: string;
}> {
  name = "EdenredDataModel";
  route = "apiEdenredData";
  defaults() {
    return {
      sync: EdenredSyncState.PENDING_STATE,
      checked: false, // default state
      availableAmount: "0",
      login: "",
      disabled: false,
    };
  }
  private static instance: EdenredDataModel;
  static getInstance() {
    return EdenredDataModel.instance || new EdenredDataModel();
  }
  initialize() {
    const model = this; // singleton ref
    EdenredDataModel.instance = model;

    model.softFetch();
    model.set("sync", EdenredSyncState.PENDING_STATE);
    model.on("change:checked", model.changeChecked);
    model.on("sync", model.updateStatus);
    model.on("error", model.onError);

    // @ts-ignore
    window.edenred = model;
  }
  changeChecked() {
    this.setLocalStorageItem();
  }
  updateStatus() {
    this.set({ sync: EdenredSyncState.FETCHED_STATE });
  }
  onError() {
    this.set({ sync: EdenredSyncState.ERROR_STATE });
  }
  setup() {
    const model = this;
    if (!hasPM()) {
      model.updateStatus();
      // si on a aucun moyen de paiement associé à EdenRed, il n'y a rien à faire
      return;
    }
    //
    const customer = app.getCustomer();
    model.listenTo(customer, "login logout", model.refresh);
    if (customer.isLogged()) {
      model.refresh();
    } else {
      model.updateStatus();
    }
  }
  static hasPM(inContext: boolean = false) {
    return hasPM(inContext);
  }
  static getPM(inContext: boolean = false) {
    return getPM(inContext);
  }
  isFetched() {
    return this.get("sync") === EdenredSyncState.FETCHED_STATE;
  }
  // static displaySwitch() {
  //   // est-ce qu'on affiche le switch
  //   return !!getPM(app.c<boolean>("_EDENRED_DISPLAY_IN_CONTEXT_", true));
  // }
  isAvailable() {
    return !!getPM(true);
  }
  hasRestriction() {
    return hasPM(true) && getPM(true)?.hasProductRestriction();
  }
  isDisabled() {
    return (
      this.get("disabled") ||
      this.hasRestriction() ||
      this.getAvailableAmount() <= 0 ||
      !hasPM(true) ||
      app.getCart().isZeroTTC()
    );
  }
  isChecked() {
    return !!this.get("checked");
  }
  static check(checked: boolean = true) {
    EdenredDataModel.instance.set("checked", checked);
  }
  static isActive() {
    const model = EdenredDataModel.getInstance();
    return hasPM(true) && model.isChecked() && !model.isDisabled();
  }
  static isSelected() {
    const model = EdenredDataModel.getInstance();
    return !model.isPending() && EdenredDataModel.isActive();
  }

  static isSelfSuffisant() {
    const model = EdenredDataModel.getInstance();
    return (
      model.isSelf() && !model.requireAltPM() && EdenredDataModel.isActive()
    );
  }
  isPending() {
    return this.get("sync") === EdenredSyncState.PENDING_STATE;
  }
  static isSelf(compare?: PaymentModeModel) {
    let r = false;
    if (hasPM() && !!compare) {
      const paymentMode = getPM() as PaymentModeModel;
      r = compare.id === paymentMode.id;
    }
    return r;
  }

  isSelf() {
    return EdenredDataModel.isSelf(getPM());
  }
  getButtonOptions(): EdenredButtonOptions {
    const model = this,
      paymentMode = getPaymentMode();
    let desc_primary = "",
      desc_secondary = "",
      tagName = "label",
      checked = model.isChecked(),
      auth = false,
      authUrl = model.get("authUrl"),
      disabled = model.isDisabled(),
      pict,
      class_list = "",
      change_label = "",
      dark = true,
      loading = false,
      showSwitcher = false,
      warnings: PMDisabledDetails[] = [],
      sync = model.get("sync");
    pict = paymentMode?.getPicture();
    if (!paymentMode) {
      desc_primary = "Invalide";
      disabled = true;
    } else if (sync === EdenredSyncState.ERROR_STATE) {
      disabled = true;
      desc_primary = paymentMode.getParam("unavailable_store_message");
    } else if (sync === EdenredSyncState.PENDING_STATE) {
      // chargement en cours
      desc_primary = app.t("Veuillez patienter", "UI");
      desc_secondary = "…";
      disabled = true;
      loading = true;
    } else if (model.requireAuth()) {
      // authentification requise
      desc_primary = paymentMode.getParam("select_btn_add_desc_primary");
      tagName = "button";
      disabled = false;
      change_label = paymentMode.getParam("select_btn_add_change_label");
      class_list = "login-edenred";
    } else {
      // authentifié
      auth = true;
      dark = false;
      desc_primary = model.get("login") || "";
      desc_secondary = model.getCreditMessage();
      change_label = "";
      showSwitcher = true;
      if (!hasPM(true)) {
        warnings.push({
          message: paymentMode.getParam("unavailable_store_message"),
          code: EPaymentModeDisableDetailsType.UNAVAILABLE_STORE,
        });
      } else if (model.hasRestriction()) {
        warnings.push({
          message: paymentMode.getRestrictionMessage(),
          code: EPaymentModeDisableDetailsType.PRODUCT_RESTRICTION,
        });
      } else if (model.requireAltPM()) {
        warnings.push({
          message:
            paymentMode.getParam("require_alt_message") +
            " " +
            paymentMode
              .getParam("require_alt_details_message")
              .replace("%s", model.formatAltDiffPrice()),
          code: EPaymentModeDisableDetailsType.REQUIRE_ALT_PM,
          variant: EAlertBlockVariant.INFO,
        });
      }
    }
    return {
      disabled,
      tagName,
      loading,
      checked,
      desc_primary,
      desc_secondary,
      showSwitcher,
      change_label,
      class_list,
      warnings,
      pict,
      dark,
      auth,
      authUrl,
    };
  }
  refresh() {
    this.set({
      sync: EdenredSyncState.PENDING_STATE,
    });
    this.remoteFetch();
  }
  requireAuth() {
    return this.get("auth") !== true;
  }
  getAvailableAmount() {
    return parseFloat(this.get("availableAmount") || "0");
  }
  formatAvailableAmount() {
    return formatPrice(this.getAvailableAmount());
  }
  getCreditMessage() {
    return (getPM() as PaymentModeModel)
      .getParam("display_credit_message")
      .replace("%s", this.formatAvailableAmount());
  }
  static getUsedAvailable() {
    // maximum utilisable, limité par le crédit, et le total panier sans frais de gestion
    return getPrices(PriceReturnType.EDENRED_TO_PAY);
  }

  static getDifference() {
    return getPrices(PriceReturnType.TOTAL_LEFT_TO_PAY);
  }
  getDifference() {
    return getPrices(PriceReturnType.TOTAL_LEFT_TO_PAY);
  }
  static getAltLeftToPay() {
    return getPrices(PriceReturnType.ALT_LEFT_TO_PAY);
  }

  getPrices(returnType: PriceReturnType) {
    return getPrices(returnType);
  }
  static requireAltPM() {
    return EdenredDataModel.getInstance().requireAltPM();
  }
  requireAltPM() {
    return this.getDifference() > 0;
  }
  formatAltDiffPrice() {
    // affiche le montant strict à payer, hors cagnotte
    return formatPrice(this.getDifference());
  }
  getName() {
    return (getPM() as PaymentModeModel).get("name");
  }
  getAccountDesc() {
    // _ACCOUNT_TICKET_RESTAU_DESCRIPTION_AUTH_ or NOT
    return (
      (getPM() as PaymentModeModel).getParam(
        "account_text_auth" + (this.requireAuth() ? "not_auth" : "auth")
      ) || ""
    );
  }
  getAccountLogout() {
    // _ACCOUNT_TICKET_RESTAU_DESCRIPTION_AUTH_ or NOT
    return (getPM() as PaymentModeModel).getParam("account_logout") || "";
  }

  static login({ login, availableAmount }: EdenredAuthSuccess) {
    const model = EdenredDataModel.getInstance();
    model.set({
      auth: true,
      checked: true,
      authUrl: undefined,
      login,
      availableAmount: availableAmount ? availableAmount.toString() : "0",
    });
    model.setLocalStorageItem();
  }
  static logout() {
    return new Promise<void>((resolve, reject) => {
      ApiRequest("EdenredLogout")
        .then(() => {
          const model = EdenredDataModel.getInstance();
          model.set({
            auth: false,
            authUrl: undefined,
            login: "",
          });
          model.setLocalStorageItem();
          model.refresh();
          resolve();
        })
        .catch(reject);
    });
  }
}

EdenredDataModel.prototype.name = "EdenredDataModel";
EdenredDataModel.prototype.route = "apiEdenredData";
EdenredDataModel.prototype.url = () => "/toolspayment/getEdenredData";
export default EdenredDataModel;
